import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { Dashboard as DashboardIcon } from "@mui/icons-material";
import { useAppContext } from "../context/AppContext";
import { WatchlistService } from "../services/watchlistService";
import { logger } from "../utils/logger";
import PageHeader from "./common/PageHeader";
import MarketIndexChart from "./charts/MarketIndexChart";
import {
  SummaryCards,
  TopStocksRanking,
  WatchlistSection,
  RecentTransactions,
} from "./dashboard/index";

const Dashboard: React.FC = () => {
  const { state } = useAppContext();
  const { currentUser, portfolio, transactions, stocks } = state;
  const [dashboardStocks, setDashboardStocks] = useState<typeof stocks>([]);

  // 使用與 Watchlist 組件相同的用戶ID邏輯
  const userId = state.currentAccountId || currentUser?.id;

  // 加載儀表板股票的函數
  const loadDashboardStocks = useCallback(async () => {
    if (!userId) {
      return;
    }

    try {
      const dashboardStockData = await WatchlistService.getDashboardStocks(
        userId
      );

      // 將股票符號對應到實際股票資料
      const matchedStocks = dashboardStockData
        .map((dashboardStock) => {
          return stocks.find((s) => s.symbol === dashboardStock.symbol);
        })
        .filter((stock): stock is (typeof stocks)[0] => stock !== undefined);

      setDashboardStocks(matchedStocks);
    } catch (error) {
      logger.component.error("Failed to load dashboard stocks", error);
    }
  }, [userId, stocks]);

  // UI-08:單一 effect 負責載入 — 舊版兩個 effect 在 stocks 每次更新時同時重跑,
  // 對 Firestore 重複請求
  useEffect(() => {
    loadDashboardStocks();
  }, [loadDashboardStocks, state.watchlist]);

  if (!currentUser || !portfolio) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          載入中...
        </Typography>
      </Box>
    );
  }

  // Build dashboard data from existing state
  const recentTransactions = transactions.slice(0, 5); // Show last 5 transactions

  // Create market summary from stocks data
  // (marketSummary calculation moved to individual components)


  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="儀表板"
        icon={DashboardIcon}
        subtitle={`歡迎回來，${currentUser.name || currentUser.displayName}`}
        actions={null}
      />

      {/* Summary Cards */}
      <SummaryCards 
        portfolio={portfolio}
        currentAccount={state.currentAccount}
        stocks={stocks}
        transactions={transactions}
        dashboardStocksCount={dashboardStocks.length}
      />

      {/* Charts and Details - 重新組織佈局 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
          },
          gap: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
        }}
      >
        {/* 大盤指數圖表 - 佔一半寬度 */}
        <MarketIndexChart
          height={700}
          autoRefresh={true}
          refreshInterval={30000}
        />

        {/* 熱門股票排行榜 - 移動到指數圖旁邊 */}
        <TopStocksRanking stocks={stocks} stateStocks={stocks} />
      </Box>

      {/* Bottom charts - 調整為雙欄佈局 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
          },
          gap: { xs: 2, sm: 3 },
        }}
      >
        {/* Watchlist Favorites */}
        <WatchlistSection stocks={dashboardStocks} stateStocks={stocks} />

        {/* Recent Transactions */}
        <RecentTransactions transactions={recentTransactions} />
      </Box>
    </Box>
  );
};

export default Dashboard;
